import { Component, ViewEncapsulation, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { Genome } from 'src/providers/genome/genome';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';
import { Dashboard } from 'src/providers/dashboard/dashboard';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import {
  RestockingOrdersService,
  RestockOrder,
  RestockOrderState,
  AssignedBooking,
  roomObjectRestockStatusFromApi,
  isRestockJobAssignable
} from 'src/shared/services/restocking-orders.service';
import { SubscriptionGateService } from 'src/shared/services/subscription-gate.service';
import { stateLabel } from 'src/shared/services/restock-order-state.pipe';

import { AddRoomItemPage } from 'src/pages/more/edit-address/add-room-item/add-room-item';
import { ItemDetailsPage } from 'src/pages/more/edit-address/room-item-details/room-item-details';
import { EditRoomItemPage } from 'src/pages/more/edit-address/room-item-details/edit-room-item/edit-room-item';
import { ContactConciergePage } from 'src/pages/concierge/contact-concierge/contact-concierge';
import { OrderRestockPage } from 'src/pages/inventory/order-restock/order-restock.page';
import { RestockOrderPage } from 'src/pages/inventory/restock-order/restock-order.page';
import { RestockOrderHistoryPage } from 'src/pages/inventory/restock-order-history/restock-order-history.page';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Events } from 'src/providers/events/events';
import { Schedule } from 'src/providers/schedule/schedule';
import { ReportIssue } from 'src/providers/report-issue/report-issue';
import { BookService } from 'src/shared/services/book.service';

import { restockingOfficeMockData } from 'src/shared/constants/onboarding/restocking';
import { restockingMockDataResidential } from 'src/shared/constants/onboarding/restocking-residential';

interface OrderCardVM {
  id: number;
  order: RestockOrder;
  title: string;
  propertyName: string;
  addressId: number;
  addressColor: string;
  metaLine: string;
  // Ordered/delivered orders can be attached to a job so a pro restocks them.
  canAssignJob: boolean;
  showJobSelect: boolean;
  // Picker options (attachment key = booking id, matching how maintenance
  // attaches). `filteredJobItems` is what the dropdown shows after the search
  // box filters `jobItems`. See card gCUZuCjf.
  jobItems: JobPickerItem[];
  filteredJobItems: JobPickerItem[];
  jobSearch: string;
  jobItemsLoaded: boolean;
  // Jobs this order is already attached to (one chip per active attachment).
  assignedChips: { bookingId: number; label: string; icon: string; proInitials: string; removable: boolean }[];
}

// A rich job option in the "Assign to Job" dropdown.
interface JobPickerItem {
  bookingId: number;
  label: string;        // "Sep 9, 2026 10:30"
  searchText: string;   // lowercased label + pro name, for the search box
  statusLabel: string;  // e.g. "Scheduled" (from getJobStatusData)
  proInitials: string;
  icon: string;         // service category icon
}

@Component({
    selector: 'inventory-page',
    templateUrl: './inventory.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./inventory.scss'],
    standalone: false
})

export class InventoryPage implements OnInit, OnDestroy {
  panelClosedSubscription: Subscription;
  shownRows: any = [];
  allRows: any = [];
  readonly desktopWidthContent: string = scrollContentWidth.LARGE;
  form: UntypedFormGroup;
  headers: any = ['Item', 'Property', 'Current #', 'Min #', 'Status', 'last-column'];
  showLoadingSpinner: boolean;
  loaded: boolean;
  addressResponse: any;
  addressId: any;
  addressItems: any;
  propertyForm: UntypedFormGroup;
  totalRows: number;
  totalAllRows: number;
  pageIndex = 1;
  signUpForm: UntypedFormGroup;
  showAboutThisPage: boolean;
  hasProperties = false;
  searchTerm: string;
  didCheckOnboarding: boolean;
  searchPageIndex: number;
  agentActivityCount = 3;
  @ViewChild('tidyTable') tidyTable: any;
  backPage = 'more';
  boundGoToOrderStock = this.goToOrderStock.bind(this);

  // Category icon per attached booking, looked up from the address's jobs so the
  // assigned-job chips can show a service icon (the order payload's
  // assigned_bookings don't carry one).
  private jobMetaByBookingId = new Map<number, { icon: string; proInitials: string }>();

  selectedTab = 'orders';
  readonly tabs = [
    { value: 'orders', label: 'Orders', icon: 'assets/svg/cart-outline.svg' },
    { value: 'items', label: 'Items', icon: 'assets/svg/cube-outline.svg' }
  ];

  // Orders are listed as status lanes: cards drop into the next lane as the
  // order progresses. Mirrors the Account Setup board in concierge.
  // Public so the loading skeleton can render the real lane headers.
  readonly orderLaneConfig: { id: string; label: string; icon: string; states: RestockOrderState[] }[] = [
    { id: 'review', label: 'Concierge Review', icon: 'assets/svg/eye-outline.svg', states: ['preview', 'finalizing'] },
    { id: 'placed', label: 'Order Placed', icon: 'assets/svg/cart-outline.svg', states: ['ordered'] },
    { id: 'delivered', label: 'Shipment Delivered', icon: 'assets/svg/cube-outline.svg', states: ['delivered'] },
    { id: 'restocked', label: 'Items Restocked', icon: 'assets/svg/checkmark-circle-outline.svg', states: ['restocked'] }
  ];
  // Placeholder cards per lane while loading, indexed like orderLaneConfig.
  readonly skeletonLaneCards = [[0, 1], [0], [0], [0]];
  orderLanes: {
    id: string;
    label: string;
    icon: string;
    cards: OrderCardVM[];
  }[] = [];

  constructor(
    private currentAddress: CurrentAddress,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public windowService: WindowService,
    private util: Util,
    private client: Client,
    private dashboard: Dashboard,
    private aboutThisPageService: AboutThisPageService,
    private onboardingProvider: OnboardingProvider,
    private agentsProvider: AgentsProvider,
    private events: Events,
    private demoBlock: DemoBlockService,
    private subscriptionGate: SubscriptionGateService,
    private restockingOrders: RestockingOrdersService,
    private schedule: Schedule,
    private reportIssue: ReportIssue,
    private bookService: BookService,
  ) {
    this.form = this.fb.group({
      search: [''],
    });
    this.propertyForm = this.fb.group({
      property: ['', Validators.required],
    });
    this.signUpForm = this.fb.group({
      whereToOrder: ['', Validators.required],
    });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300)
    ).subscribe(value => {
      this.updateSearch({ search: value });
      this.refreshOrderLanes();
    });
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('restocking');
    // Keep the Orders lanes in sync as orders mutate (e.g. after submitting a
    // preview cart from the order flow).
    this.restockingOrders.orders$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.refreshOrderLanes();
      });
  }

  async ngOnInit() {
    const cameFrom = localStorage.getItem('cameFromPage');
    if (cameFrom === 'properties' || cameFrom === 'more') {
      this.backPage = cameFrom;
    }
    try {
      await this.loadPage();
    } catch (err) {
      console.error('Error loading inventory page:', err);
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('restocking');
    await this.getPageData();
    this.showLoadingSpinner = false;
    this.loaded = true;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/restocking').then(async () => {
        this.pageIndex = 1;
        this.searchTerm = '';
        this.form.get('search')?.setValue('');
        this.searchPageIndex = 1;
        await this.loadPage(true);
      });
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.shownRows = [];
    this.allRows = [];
    this.totalRows = 0;
    this.totalAllRows = 0;
    this.loadAddresses();
    this.loadPage(true);
  }

  async loadAddresses() {
    this.addressResponse = await this.client.getAddresses();
  }

  ngOnDestroy() {
    this.cleanup();
  }

  cleanup() {
    if (this.panelClosedSubscription) {
      this.storage.delete('shouldReloadPageAfterClosingSideRail');
      this.panelClosedSubscription.unsubscribe();
    }
  }

  async getPageData() {
    try {
      await this.getPropertyItems();
      this.buildHeaders();
      const savedPropertyId = localStorage.getItem('inventoryPropertyFilter');
      const lowOnly = localStorage.getItem('inventoryLowOnly');
      this.propertyForm.get('property').setValue(savedPropertyId ? Number(savedPropertyId) : 'all');
      if (savedPropertyId) {
        localStorage.removeItem('inventoryPropertyFilter');
      }
      await this.refreshRestockOrders();
      await this.getInventory();
      if (lowOnly) {
        this.shownRows = this.shownRows.filter((row) => row['Current #'].value < row['Min #'].value);
        localStorage.removeItem('inventoryLowOnly');
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async getPropertyItems() {
    this.addressResponse = await this.client.getAddresses();
    if (this.addressResponse.length === 0) {
      return [];
    }
    this.addressId = this.client.getSelectedAddressId(this.addressResponse);
    this.addressItems = this.client.parseAddressList(this.addressResponse, true);
    this.addressItems.unshift({
      value: 'all',
      viewValue: 'All Properties'
    })
    this.addressItems.pop();
  }

  async changeProperty() {
    this.pageIndex = 1;
    this.searchPageIndex = 1;
    this.showLoadingSpinner = true;
    this.refreshOrderLanes();
    try {
      await this.refreshRestockOrders();
      await this.getInventory();
    } finally {
      this.showLoadingSpinner = false;
    }
  }

  async updateCurrentQuantity(object, newValue) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    try {
      this.showLoadingSpinner = true;
      await this.genome.updateRoomObject(object.id, {current_quantity: newValue});
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    } finally {
      this.showLoadingSpinner = false;
    }
  }

  async updateMinQuantity(object, newValue) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    // The item form refuses a target below the min, so a min raised past the
    // target here would leave the item unsaveable from that form. Lowering a min
    // is always allowed: many items already have a target below their min.
    const hasTarget = object.max_quantity !== null && object.max_quantity !== undefined;
    if (hasTarget && newValue > object.max_quantity && newValue > object.min_quantity) {
      this.restoreMinQuantityCell(object);
      this.util.showError('The min quantity must be less than or equal to the target', 10000);
      return;
    }
    try {
      this.showLoadingSpinner = true;
      await this.genome.updateRoomObject(object.id, {min_quantity: newValue});
      object.min_quantity = newValue;
    } catch (err) {
      this.restoreMinQuantityCell(object);
      this.util.showError(this.getMinQuantityErrorMessage(err), 10000);
    } finally {
      this.showLoadingSpinner = false;
    }
  }

  // The table writes the typed value into the cell before onEdit runs, so a
  // refused edit has to put the saved min (and its low-stock icon) back.
  restoreMinQuantityCell(object) {
    const row = this.shownRows?.find((shownRow) => shownRow.id === object.id);
    if (!row) {
      return;
    }
    row['Min #'].value = object.min_quantity;
    if (Number(row['Current #'].value) < Number(object.min_quantity)) {
      row['Current #'].icon = 'assets/img/request-failed.svg';
    } else {
      delete row['Current #'].icon;
    }
    this.tidyTable?.refreshTableData();
  }

  getMinQuantityErrorMessage(err): string {
    const message = `${err?.error?.message || err?.message || ''}`.replace(/^Validation failed:\s*/i, '');
    if (/opportunistic quantity must be greater than or equal to min quantity/i.test(message)) {
      return 'The warning level must be greater than or equal to the min quantity';
    }
    return message || 'Something went wrong';
  }

  getPropertyName(address) {
    if (address.address_name) {
      return address.address_name;
    } else if (address.address2 && address.address2 !== '') {
      return `${address.address1  }, ${  address.address2}`;
    } else {return address.address1
      return address.address1;
    }
  }

  buildHeaders() {
    this.headers = [
      'Item',
      'Property',
      'Current #',
      'Min #',
      'Status',
      'last-column'
    ];
  }

  goToRoomItemPage(object) {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      addressId: object.address.id,
      itemId: object.id,
      cameFromEditItem: false,
      cameFromRoute: 'restocking'
    };
    localStorage.setItem('roomItemBackPage', 'restocking');
    this.goToPage(`item-details/${object.id}`, params, ItemDetailsPage);
  }

  goToEditRoomItemPage(object) {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      addressId: object.address.id,
      itemId: object.id,
      cameFromRoute: 'restocking'
    };
    localStorage.setItem('roomItemBackPage', 'restocking');
    this.goToPage('edit-room-item', params, EditRoomItemPage);
  }

  goToPropertyPage(addressId) {
    if (this.showAboutThisPage) {
      return;
    }
    this.navCtrl.navigateForward(`properties?propertyId=${addressId}`);
  }

  sortChanged(sort) {
    if (sort.direction === 'asc' || sort.direction === '') {
      this.allRows.sort((a, b) => a[sort.active].value > b[sort.active].value ? 1 : -1);
      // Create a new array reference to trigger Angular change detection
      this.shownRows = [...this.allRows];
    } else {
      this.allRows.sort((a, b) => a[sort.active].value < b[sort.active].value ? 1 : -1);
      // Create a new array reference to trigger Angular change detection
      this.shownRows = [...this.allRows];
    }
  }

  async goToAddRoomItemPage() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    if (await this.subscriptionGate.gate()) { return; }
    const params = {
      showAddressSelect: true,
      showConsumables: true
    }
    this.goToPage('add-item', params, AddRoomItemPage);
  }

  goToAddConciergeTask() {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      title: 'Concierge Action',
      type: 'support.concierge_task',
      isAddingRoomItem: true
    }
    this.goToPage('contact-concierge', params, ContactConciergePage);
  }

  async goToPage(url, params, component) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(async () => {
        const shouldReloadPageAfterClosingSideRail = await this.storage.retrieve('shouldReloadPageAfterClosingSideRail');
        if (shouldReloadPageAfterClosingSideRail) {
          this.showLoadingSpinner = true;
          this.pageIndex = 1;
          this.searchPageIndex = 1;
          this.searchTerm = '';
          this.form.get('search')?.setValue('');
          await this.getPageData();
          this.showLoadingSpinner = false;
          this.storage.delete('shouldReloadPageAfterClosingSideRail');
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  async goToOrderStock() {
    if (this.showAboutThisPage) {
      return;
    }
    const { propertyId, propertyName } = this.getOrderProperty();
    this.restockingOrders.clearCartDraft();
    await this.goToPage('order-restock', { propertyId, propertyName }, OrderRestockPage);
  }

  async goToOrderHistory() {
    if (this.showAboutThisPage) {
      return;
    }
    await this.goToPage('restock-order-history', {}, RestockOrderHistoryPage);
  }

  async goToRestockOrder(order: RestockOrder) {
    if (this.showAboutThisPage) {
      return;
    }
    await this.goToPage(`restock-order/${order.id}`, { orderId: order.id }, RestockOrderPage);
  }

  selectTab(tab: string) {
    this.selectedTab = tab;
  }

  // Groups orders into status lanes, honouring the property filter and the
  // search box (order number or property name).
  refreshOrderLanes() {
    const propertyId = this.getFilterPropertyId();
    const term = (this.form?.get('search')?.value || '').trim().toLowerCase();
    const orders = this.restockingOrders.getOrders(propertyId).filter((order) => {
      if (!term) {
        return true;
      }
      return String(order.id).includes(term)
        || (order.propertyName || '').toLowerCase().includes(term);
    });
    this.orderLanes = this.orderLaneConfig.map((lane) => ({
      id: lane.id,
      label: lane.label,
      icon: lane.icon,
      cards: orders
        .filter((order) => lane.states.includes(order.state))
        .map((order) => {
          const itemCount = order.items?.length || 0;
          const existing = this.findCard(order.id);
          return {
            id: order.id,
            order,
            title: `Order #${order.id}`,
            propertyName: order.propertyName,
            addressId: order.propertyId,
            addressColor: this.addressResponse?.find((address) => address.id === order.propertyId)?.color || '#1E2125',
            metaLine: `${itemCount} ${itemCount === 1 ? 'item' : 'items'} · ${this.formatShortDate(order.createdAt)}`,
            canAssignJob: order.state === 'ordered' || order.state === 'delivered',
            // Keep the picker open (and its loaded jobs) across rebuilds.
            showJobSelect: existing?.showJobSelect || false,
            jobItems: existing?.jobItems || [],
            filteredJobItems: existing?.filteredJobItems || [],
            jobSearch: existing?.jobSearch || '',
            jobItemsLoaded: existing?.jobItemsLoaded || false,
            assignedChips: (order.assignedBookings || []).map((booking) => ({
              bookingId: booking.bookingId,
              label: this.buildAssignedChipLabel(booking),
              icon: this.jobMetaByBookingId.get(booking.bookingId)?.icon || 'assets/svg/calendar-outline.svg',
              proInitials: this.initialsFrom(booking.proName),
              removable: booking.state === 'pending',
            })),
          };
        })
    }));
  }

  private findCard(orderId: number): OrderCardVM | undefined {
    for (const lane of this.orderLanes) {
      const match = lane.cards.find((card) => card.id === orderId);
      if (match) {
        return match;
      }
    }
    return undefined;
  }

  // Guarded card click: while the "Assign to Job" dropdown is open, a stray
  // click shouldn't navigate to the order detail.
  onCardClick(card: OrderCardVM) {
    if (card.showJobSelect) {
      return;
    }
    this.goToRestockOrder(card.order);
  }

  // Toggles the job picker for an ordered/delivered order, loading that
  // address's scheduled jobs each time it opens.
  async toggleJobSelect(card: OrderCardVM, event?: Event) {
    event?.stopPropagation();
    if (card.showJobSelect) {
      card.showJobSelect = false;
      return;
    }
    card.showJobSelect = true;
    card.jobSearch = '';
    card.jobItemsLoaded = false;
    // Reload each time so jobs already attached to this order are excluded.
    card.jobItems = await this.loadJobItems(card);
    card.filteredJobItems = card.jobItems;
    card.jobItemsLoaded = true;
  }

  cancelJobSelect(card: OrderCardVM, event?: Event) {
    event?.stopPropagation();
    card.showJobSelect = false;
  }

  onJobSearch(card: OrderCardVM, term: string) {
    card.jobSearch = term;
    const needle = (term || '').trim().toLowerCase();
    card.filteredJobItems = needle
      ? card.jobItems.filter((item) => item.searchText.includes(needle))
      : card.jobItems;
  }

  // Builds the rich picker options for a card. The attachment key is the booking
  // id; on a raw calendar-view job it's nested at `job.booking.id`, with
  // fallbacks for the shapes seen elsewhere. Jobs already attached to this order
  // are filtered out.
  private async loadJobItems(card: OrderCardVM): Promise<JobPickerItem[]> {
    let items: JobPickerItem[] = [];
    try {
      const assignedBookingIds = new Set(card.assignedChips.map((chip) => chip.bookingId));
      const jobs = await this.schedule.getJobs(card.addressId);
      items = (jobs || [])
        .map((job) => ({ job, bookingId: this.resolveBookingId(job) }))
        .filter((entry) => isRestockJobAssignable(entry.job)
          && entry.bookingId
          && !assignedBookingIds.has(entry.bookingId))
        .map(({ job, bookingId }) => this.buildJobPickerItem(job, bookingId));
    } catch {
      items = [];
    }
    return items;
  }

  private buildJobPickerItem(job: any, bookingId: number): JobPickerItem {
    const proName = job?.homekeeper_jobs?.[0]?.homekeeper?.name || job?.potential_homekeeper?.name || '';
    const label = `${job?.date || ''} ${job?.start_time || ''}`.trim() || `Job #${job?.id}`;
    const statusData = this.reportIssue.getJobStatusData(job?.homekeeper_jobs, job?.job_progress, job);
    return {
      bookingId,
      label,
      searchText: `${label} ${proName}`.toLowerCase(),
      statusLabel: this.titleCase(statusData?.name),
      proInitials: this.initialsFrom(proName),
      icon: job?.service_icon_url || 'assets/svg/calendar-outline.svg',
    };
  }

  private initialsFrom(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '';
    }
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase();
  }

  private titleCase(value: string): string {
    if (!value) {
      return '';
    }
    return value.replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private resolveBookingId(job: any): number | undefined {
    return job?.booking?.id
      || job?.booking_id
      || job?.object?.booking?.id
      || job?.template_data?.booking?.id
      || undefined;
  }

  async onJobSelected(card: OrderCardVM, bookingId: number, event?: Event) {
    event?.stopPropagation();
    if (!bookingId) {
      return;
    }
    card.showJobSelect = false;
    try {
      await this.restockingOrders.assignBooking(card.id, bookingId);
      this.util.showSuccess('Order attached to job.');
    } catch (error) {
      this.util.showError(error?.message || 'Could not attach the order to that job.');
    }
  }

  // Detach a pending attempt; the order card drops that chip.
  async unassignJob(card: OrderCardVM, bookingId: number, event?: Event) {
    event?.stopPropagation();
    try {
      await this.restockingOrders.unassignBooking(card.id, bookingId);
    } catch (error) {
      this.util.showError(error?.message || 'Could not remove the assignment.');
    }
  }

  // A short chip label for an attachment, e.g. "Aug 15" (+ outcome once the job
  // is done). The pro is shown as an initials avatar, not in the label.
  private buildAssignedChipLabel(booking: AssignedBooking): string {
    let label = this.formatShortDate(booking.date) || `Booking #${booking.bookingId}`;
    if (booking.state === 'restocked') {
      label += ' (Restocked)';
    } else if (booking.state === 'not_restocked') {
      label += ' (Not restocked)';
    }
    return label;
  }

  // Opens the real Book Job flow for this order's property. Routes through
  // BookService (same entry point the Schedule page's "Add Job" uses) so the
  // panel gets the BookJobPage component + gating/variant params — navigating to
  // 'book-job' directly just opens a blank panel.
  async goToAddJob(card: OrderCardVM, event?: Event) {
    event?.stopPropagation();
    card.showJobSelect = false;
    const address = this.addressResponse?.find((addr) => addr.id === card.addressId) || this.addressResponse?.[0];
    if (!address) {
      return;
    }
    await this.storage.save('bookJobBackPage', 'restocking');
    const bookingType = address?.plans?.length > 0 ? 'add_one_time_job' : 'add_job';
    await this.bookService.goToBookJob({ address, bookingType });
  }

  // Fetch restock orders for the current property filter into the reactive store.
  // Non-blocking: a failure just means the in-flight indicators don't render, so
  // the inventory table still loads.
  private async refreshRestockOrders() {
    try {
      const orders = await this.restockingOrders.loadOrders(this.getFilterPropertyId());
      await this.hydrateAssignedBookings(orders);
      await this.loadAssignedJobMeta(orders);
      // Rebuild lanes so chips pick up the freshly-loaded category icons.
      this.refreshOrderLanes();
    } catch {
      // ignore — inventory renders without in-flight indicators
    }
  }

  // The list endpoint omits assigned_bookings (it's detail-only on the backend,
  // to avoid an N+1 there). Fetch detail for the assignable lanes so their cards
  // can show the attached-job chips. Bounded to ordered/delivered orders and
  // best-effort — a failed fetch just leaves that card without chips.
  private async hydrateAssignedBookings(orders: RestockOrder[]) {
    const assignable = orders.filter((order) => order.state === 'ordered' || order.state === 'delivered');
    await Promise.all(
      assignable.map((order) => this.restockingOrders.getOrderDetail(order.id).catch(() => undefined))
    );
  }

  // Builds a booking id → { icon, initials } map from each assignable address's
  // jobs, so assigned-job chips can show a category icon (the order payload's
  // assigned_bookings carry pro name + date, but no service icon). One getJobs
  // call per unique address; best-effort.
  private async loadAssignedJobMeta(orders: RestockOrder[]) {
    const addressIds = [
      ...new Set(
        orders
          .filter((order) => order.state === 'ordered' || order.state === 'delivered')
          .map((order) => order.propertyId)
      ),
    ];
    await Promise.all(addressIds.map(async (addressId) => {
      try {
        const jobs = await this.schedule.getJobs(addressId);
        (jobs || []).forEach((job) => {
          const bookingId = this.resolveBookingId(job);
          if (!bookingId) {
            return;
          }
          this.jobMetaByBookingId.set(bookingId, {
            icon: job?.service_icon_url || 'assets/svg/calendar-outline.svg',
            proInitials: this.initialsFrom(job?.homekeeper_jobs?.[0]?.homekeeper?.name || job?.potential_homekeeper?.name || ''),
          });
        });
      } catch {
        // best-effort — chips fall back to the default calendar icon
      }
    }));
  }

  // "All Properties" shows every order; a specific filter scopes to that property.
  private getFilterPropertyId(): number | undefined {
    const selected = this.propertyForm?.get('property')?.value;
    return selected && selected !== 'all' ? Number(selected) : undefined;
  }

  // A new order always targets a concrete property: the selected one, or the
  // first real address when the filter is set to "All Properties".
  private getOrderProperty(): { propertyId: number; propertyName: string } {
    const selected = this.propertyForm?.get('property')?.value;
    if (selected && selected !== 'all') {
      const match = this.addressItems?.find((item) => item.value === selected);
      return { propertyId: Number(selected), propertyName: match?.viewValue };
    }
    const firstAddress = this.addressItems?.find((item) => item.value !== 'all');
    return { propertyId: Number(firstAddress?.value), propertyName: firstAddress?.viewValue };
  }

  private formatShortDate(iso: string): string {
    if (!iso) {
      return '';
    }
    // A date-only string (YYYY-MM-DD) parses as UTC midnight, which shifts back a
    // day when rendered in a behind-UTC timezone. Parse those as a local date.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const date = dateOnly
      ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
      : new Date(iso);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  onTableColumnClick(event: {column: string, element: any}) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    // The Status chip deep-links to its order; every other cell opens the item.
    if (event.column === 'Status' && event.element['Status']?.orderId) {
      this.goToRestockOrderById(event.element['Status'].orderId);
      return;
    }
    this.goToRoomItemPage(this.findOriginalObject(event.element));
  }

  findOriginalObject(element: any) {
    // Find the original object from roomObjects that matches this row
    const address = this.addressResponse.find(addr => addr.id === element['Property'].addressId);
    return {
      address,
      id: element.id // We need to add this to the row data
    };
  }

  async updateSearch({search}) {
    this.searchTerm = search.toLowerCase();
    this.searchPageIndex = 1;
    if (search.length < 2) {
      this.totalRows = this.totalAllRows;
      return this.shownRows = this.allRows;
    }
    this.showLoadingSpinner = true;
    await this.getInventory();
    this.showLoadingSpinner = false;
  }

  async pageChange() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.searchTerm) {
      this.searchPageIndex += 1;
    } else {
      this.pageIndex += 1;
    }
    this.tidyTable.isLoading = true;
    await this.getInventory(true);
    this.tidyTable.isLoading = false;
  }

  async getInventory(isPageChange = false): Promise<void> {
    try {
      let roomObjects = [];
      if (this.showAboutThisPage) {
        const accountType = localStorage.getItem('accountType');
        const isCompany = accountType === 'company';
        roomObjects = isCompany ? restockingOfficeMockData : restockingMockDataResidential;
        this.totalRows = roomObjects.length;
      }
      if (!this.showAboutThisPage) {
        const selectedProperty = this.propertyForm.get('property').value;
        const params: any = {page: this.searchTerm ? this.searchPageIndex : this.pageIndex, per_page: 100, consumable: true, search: this.searchTerm};
        if (selectedProperty && selectedProperty !== 'all') {
          params.address_id = selectedProperty;
        }
        const roomObjectsResp = await this.genome.getInventoryRoomObjects(params);
        roomObjects = roomObjectsResp.body;
        this.totalRows = roomObjectsResp.totalRecords;
      }
      // The shared demo account has no room-objects of its own; if the demo data
      // layer didn't supply any, fall back to curated sample data so the page
      // never renders blank in a demo session.
      if (this.demoBlock.isDemo && !roomObjects?.length) {
        const isCompany = localStorage.getItem('accountType') === 'company';
        roomObjects = isCompany ? restockingOfficeMockData : restockingMockDataResidential;
        this.totalRows = roomObjects.length;
      }
      this.refreshOrderLanes();
      const rows = this.buildRows(roomObjects);
      if (isPageChange) {
        this.shownRows = [
          ...this.shownRows,
          ...rows
        ];
      } else {
        this.shownRows = rows;
      }
      if (!this.searchTerm) {
        this.allRows = this.shownRows;
        this.totalAllRows = this.totalRows;
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  buildRows(roomObjects) {
    const rows = [];
    roomObjects.map((object) => {
      const cell = {
        'Item': {
          value: object.name,
          icon: object?.model?.category?.default_icon_url || 'assets/svg/home-outline.svg'
        },
        'Property': {
          value: this.getPropertyName(object.address),
          action: this.goToPropertyPage.bind(this, object.address.id),
          addressId: object.address.id,
          color: this.addressResponse.find((address) => address.id === object.address.id)?.color || '#1E2125'
        },
        'Current #': {
          value: object.current_quantity || 0,
          editable: true,
          showIconOnRight: true,
          onEdit: this.updateCurrentQuantity.bind(this, object)
        },
        'Min #': {
          value: object.min_quantity,
          editable: true,
          onEdit: this.updateMinQuantity.bind(this, object)
        },
        'Status': this.buildStatusCell(object),
        'last-column': {
          value: '',
          action: null,
          hasChevron: true
        },
        id: object.id
      }
      if (object.current_quantity < object.min_quantity) {
        cell['Current #']['icon'] = 'assets/img/request-failed.svg';
      }
      rows.push(cell);
    });
    return rows;
  }

  // The badge state is the backend's derived `restock_status` (gotidy #8380):
  // an in-flight order's state (preview/finalizing/ordered/delivered), else
  // 'out_of_stock' when nothing is on hand, else null (in stock / untracked).
  // When the field is absent (backend predates it), fall back to deriving the
  // same precedence from the deployed `current_restock_order` + current_quantity
  // so the column still works. `null` (present) = genuinely no status.
  private restockBadge(object): string | null {
    if (object.restock_status !== undefined) {
      return object.restock_status;
    }
    const order = roomObjectRestockStatusFromApi(object.current_restock_order);
    if (order) {
      return order.state;
    }
    // Strictly 0 — a null/undefined quantity is untracked, not out of stock.
    return object.current_quantity === 0 ? 'out_of_stock' : null;
  }

  // Status cell: lifecycle chip + inline meta, rendered as trusted HTML (the
  // table pipes cell values through innerHTML). exportValue keeps CSVs clean.
  // We render the badge string directly; `current_restock_order` still supplies
  // the ETA / substitution detail and the order id for the click-through.
  private buildStatusCell(object) {
    const badge = this.showAboutThisPage ? null : this.restockBadge(object);
    if (!badge) {
      return { value: '<span class="restock-cell-none">—</span>', exportValue: '' };
    }
    if (badge === 'out_of_stock') {
      return {
        value: '<span class="restock-status-chip out-of-stock">Out of Stock</span>',
        exportValue: 'Out of Stock'
      };
    }
    // Order-backed state: label + colors come from the shared order vocabulary;
    // ETA and substitution detail come from the preloaded order object.
    const state = badge as RestockOrderState;
    const order = roomObjectRestockStatusFromApi(object.current_restock_order);
    const label = stateLabel(state);
    const eta = state === 'ordered' && order?.etaDate ? `Arrives ~${this.formatShortDate(order.etaDate)}` : '';
    const substituted = !!order?.resolved?.substitutionNote;
    const parts = [`<span class="restock-status-chip ${state}">${label}</span>`];
    if (eta) {
      parts.push(`<span class="restock-cell-meta">${eta}</span>`);
    }
    if (substituted) {
      parts.push('<span class="restock-cell-substituted">Substituted</span>');
    }
    return {
      value: parts.join(''),
      exportValue: [label, eta, substituted ? 'Substituted' : ''].filter(Boolean).join(' · '),
      orderId: order?.orderId
    };
  }

  goToRestockOrderById(orderId: number) {
    if (this.showAboutThisPage) {
      return;
    }
    this.goToPage(`restock-order/${orderId}`, { orderId }, RestockOrderPage);
  }

  async exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }
    if (!this.tidyTable) {
      this.util.showError('No data to export', 5000);
      return;
    }
    try {
      const loadingIndicator = await this.util.showLoading({message: 'Preparing export...'});
      const selectedProperty = this.propertyForm.get('property').value;
      const params: any = {page: 1, per_page: this.totalRows, consumable: true, search: this.searchTerm};
      if (selectedProperty && selectedProperty !== 'all') {
        params.address_id = selectedProperty;
      }
      const roomObjectsResp = await this.genome.getInventoryRoomObjects(params);
      const allRoomObjects = roomObjectsResp.body;
      const allRows = this.buildRows(allRoomObjects);
      const exportDataSource = new MatTableDataSource(allRows);
      this.tidyTable.exportCsv(exportDataSource);
      loadingIndicator?.dismiss();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

}
